import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import InputGroup from '../../../../../../../components/global/InputGroup';
import UserIcon from '../../../../../../../components/icons/UserIcon';
import Button from '../../../../../../../components/global/Button';
import { isValidUsername } from '../../../../../../../util/account';
import { getAccountFirstLastNameFromString } from './helpers';
import { getFormikTouchedErrors } from '../../../../../../../util/formik';
import Alert from '../../../../../../../components/global/Alert';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  submitting: PropTypes.bool,
  submitError: PropTypes.string,
  skipUsername: PropTypes.bool,
  children: PropTypes.func,
};

const AccountSetupForm = ({
  account,
  onFormSubmit,
  className,
  submitting,
  submitError,
  skipUsername,
  children,
}) => {
  const onValidate = values => {
    const errors = {};

    if (!isValidUsername(values.username) && !skipUsername) {
      errors.username =
        'Please enter a username that is at least three characters long, and only contains letters, numbers, and underscores.';
    }

    if (!values.firstName || !values.lastName) {
      errors.lastName = 'Please enter a first and last name.';
    }

    return errors;
  };

  const onSubmit = values => {
    onFormSubmit({
      username: skipUsername ? undefined : values.username,
      name: `${values.firstName.trim()} ${values.lastName.trim()}`,
    });
  };

  return (
    <Formik
      initialValues={{
        username: account.username || '',
        ...getAccountFirstLastNameFromString(account.name),
      }}
      onSubmit={onSubmit}
      validate={onValidate}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
      }) => {
        const formikErrors = Object.values(
          getFormikTouchedErrors(errors, touched),
        );

        return (
          <form method="post" className={className} onSubmit={handleSubmit}>
            {!skipUsername && (
              <InputGroup
                id="username"
                type="text"
                name="username"
                label="Username"
                labelSrOnly
                addon="@"
                placeholder="Choose a username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                wrapperClassName="mb-3"
                required
              />
            )}

            <InputGroup
              id="firstname"
              type="text"
              name="firstName"
              label="First name"
              labelSrOnly
              addon={<UserIcon />}
              placeholder="First name"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              wrapperClassName="mb-2"
              required
            />

            <InputGroup
              id="lastname"
              type="text"
              name="lastName"
              label="Last name"
              labelSrOnly
              addon={<UserIcon />}
              placeholder="Last name"
              value={values.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              wrapperClassName="mb-2"
            />

            {(submitError || formikErrors.length > 0) && (
              <Alert type="danger">
                {submitError ||
                  formikErrors.map((message, index) => (
                    <p key={index}>{message}</p>
                  ))}
              </Alert>
            )}

            {children ? (
              children({ handleSubmit })
            ) : (
              <Button
                type="submit"
                className="text-extra-bold px-3 mt-4"
                btnBlock
                disabled={submitting}
              >
                {submitting ? 'Loading…' : 'Complete Registration'}
              </Button>
            )}
          </form>
        );
      }}
    </Formik>
  );
};

AccountSetupForm.propTypes = propTypes;

export default AccountSetupForm;
